/*
Grilla Expandible, con un boton que permite agregar datos a la cola.
Le pasamos el estado para poder llamar a la que crea vehioculos.
Usado tambien en la nota de venta

*/
import React, { useState, useEffect, SetStateAction } from 'react'

import Vehicle from '../interfaces/Vehicle'
import useVehicleFetcher from '../hooks/useVehicleFetcher'
import { FormatProps, handleInputChangeFormat } from '../utils/handleInputChangeFormat'
import { Rule, validateFields } from '../utils/validateForm'
import { formatWithThousandSep } from '../utils/utils'
import { generatePayVehicle } from '../utils/reservationNvvShared'

import InputField from './InputField'
import { ButtonActionShort } from './Buttons'
import { Warning } from './MessageInfo'
import ConfirmationModal from './ConfirmationModal'
import VehicleCard from './VehicleCard'
import { PayVehicle } from './ReservationNvvEdit'

interface GridExpProps {
  title: string
  array: PayVehicle[]
  patenteReservada: string
  onVehicleCreate: () => void
  onPaymentsChange: (payments: PayVehicle[]) => void
  vehicleMakeDescription: (vehicle: Vehicle | null) => string
  vehShowVehicleEdit: boolean
  setVehShowVehicleEdit: (sw: boolean) => void
  vehNewPayment: PayVehicle | null
  setVehNewPayment: (pay: SetStateAction<PayVehicle | null>) => void
  vehErrors: { [key: string]: string }
  setVehErrors: (err: { [key: string]: string }) => void
  vehVehicle: Vehicle | null
  setVehVehicle: (veh: Vehicle | null) => void
}

const ReservationVehiculo: React.FC<GridExpProps> = ({
  title,
  array,
  patenteReservada,
  onVehicleCreate,
  onPaymentsChange,
  vehicleMakeDescription,
  vehShowVehicleEdit,
  setVehShowVehicleEdit,
  vehNewPayment,
  setVehNewPayment,
  vehErrors,
  setVehErrors,
  vehVehicle,
  setVehVehicle,
}) => {
  const [payments, setPayments] = useState<PayVehicle[]>(array)
  const { errorVehicle, fetchVehiclesByPlate } = useVehicleFetcher()
  const [warningMessage, setWarningMessage] = useState<string>('')
  const iniVehPayment: PayVehicle = generatePayVehicle()
  useEffect(() => {
    onPaymentsChange(payments)
  }, [payments])

  /* Cuando hay reserva , se gatilla el siguiente */
  useEffect(() => {
    setPayments(array)
  }, [array])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    if (name === 'patente') {
      e.target.value = value.toUpperCase()
    }
    const formatProps: FormatProps[] = [{ name: 'price', name_format: 'price_format', isDecimal: false }]

    handleInputChangeFormat<PayVehicle>(e, vehNewPayment, setVehNewPayment, formatProps)
  }
  // Boton Agregar Vehiculos
  const handleAgregarClick = () => {
    setVehErrors({})
    setVehNewPayment(iniVehPayment)
    setVehVehicle(null)
    setVehShowVehicleEdit(true)
  }
  const handleVehiculoAceptarClick = () => {
    if (!vehVehicle) {
      setWarningMessage('Recuerda Buscar o Crear un vehículo de parte de pago.')

      return
    }

    const fields = [{ name: 'price_format', value: vehNewPayment?.price_format, rule: Rule.GREATER_ZERO }]

    const validationErrors = validateFields(fields)

    if (Object.keys(validationErrors).length > 0) {
      setVehErrors(validationErrors) // Set errors if any

      return
    }
    setVehErrors({})
    // Que exista un vehiculo

    const value = vehicleMakeDescription(vehVehicle)

    if (vehNewPayment) {
      const updatedPayment = { ...vehNewPayment, description: value, vehicle: vehVehicle }

      // Set the updated payment and push it to payments array
      setVehNewPayment(updatedPayment)
      // Esto es para resolver un problema de la "asincronicidad "

      setPayments((prevPayments) => [...prevPayments, updatedPayment])
      // limpiamos
      setVehNewPayment(iniVehPayment)
    }
    setVehShowVehicleEdit(false)
  }
  const handlePagoCancelarClick = () => {
    setVehNewPayment(iniVehPayment)

    setVehShowVehicleEdit(false)
  }

  // Busca por patente
  const handleBuscarClick = async () => {
    const fields = [{ name: 'patente', value: vehNewPayment?.patente, rule: Rule.REQUIRED }]

    const validationErrors = validateFields(fields)

    if (Object.keys(validationErrors).length > 0) {
      setVehErrors(validationErrors) // Set errors if any

      return
    }
    setVehErrors({})

    const veh = await fetchVehiclesByPlate(vehNewPayment?.patente ?? '')

    if (!errorVehicle) {
      // Le quitamos la patente que está arriba
      const activeItems = veh.filter((item) => item.plate !== patenteReservada)

      if (activeItems.length > 0) {
        setVehVehicle(activeItems[0])
      } else {
        setVehVehicle(null)
        setWarningMessage('No existe vehículo con esa patente para usar como parte de pago.')
      }
    }
  }

  const onWarningTimeout = (): void => {
    setWarningMessage('')
  }

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)

  const onDeleteClick = (key: string) => {
    setSelectedItemId(key) // Store the item ID in the state
    setIsModalOpen(true)
  }
  const handleConfirm = () => {
    setIsModalOpen(false)
    // Perform delete action here
    setPayments((prevItems) => prevItems.filter((item) => item.key !== selectedItemId))
    console.log('Item deleted:' + selectedItemId)
    setSelectedItemId(null)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setSelectedItemId(null)
  }

  return (
    <div>
      <div className="flex">
        <div className="text-2xl w-48">{title}</div>
        <div className="flex-1">
          <ButtonActionShort label="Agregar..." handleButtonClick={handleAgregarClick} />
        </div>
      </div>
      {errorVehicle?.message}
      <div className="grid grid-cols-6 border-t">
        {payments.map((payment) => (
          <React.Fragment key={payment.key}>
            <div className="border-b pr-4 col-span-4 ">{payment.description}</div>
            <div className="border-b pr-4 text-right">${formatWithThousandSep(payment.price)}</div>
            <button
              className="border-b pr-4 cursor-pointer hover:text-red-500 focus:outline-none focus:ring-2"
              onClick={() => onDeleteClick(payment.key)}
              aria-label="Delete"
            >
              &#x1F5D1;
            </button>
          </React.Fragment>
        ))}

        {isModalOpen && (
          <ConfirmationModal
            message="¿Estás seguro de eliminar este vehículo como parte de pago?"
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        )}
      </div>
      {vehShowVehicleEdit && (
        <>
          <div className="flex flex-row space-x-4 mt-4 mb-4 items-end">
            <InputField
              label="Patente"
              name="patente"
              value={vehNewPayment?.patente}
              type="string"
              handleInputChange={handleInputChange}
              errors={vehErrors}
            />
            <ButtonActionShort label="Buscar por patente" handleButtonClick={handleBuscarClick} />
            <ButtonActionShort label="Crear..." handleButtonClick={onVehicleCreate} />
          </div>
          <div className="flex space-x-6">
            {vehVehicle && (
              <>
                <div className="flex items-center justify-center p-4 border rounded-md ">
                  <div className="flex flex-row items-center space-x-4">
                    <VehicleCard vehicle={vehVehicle} />
                  </div>
                </div>

                <div className="flex items-center justify-center">
                  <InputField
                    label="Precio de toma"
                    name="price_format"
                    value={vehNewPayment?.price_format}
                    type="string"
                    handleInputChange={handleInputChange}
                    errors={vehErrors}
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex justify-center mt-4 space-x-4">
            <ButtonActionShort label="Aceptar" handleButtonClick={handleVehiculoAceptarClick} />
            <ButtonActionShort label="Cancelar" handleButtonClick={handlePagoCancelarClick} />
          </div>
        </>
      )}
      <Warning message={warningMessage} trigger={Date.now()} onTimeout={onWarningTimeout} />
    </div>
  )
}

export default ReservationVehiculo
